/**
 * ProjectManagementTour.jsx
 * Native Ant Design 5 <Tour /> component wrapped with dynamic Dark/Light theme tokens,
 * safe target mapping, and step transition handlers.
 */

import React, { useMemo } from 'react';
import { Tour, ConfigProvider } from 'antd';
import { useTheme } from '@/context/theme';
import { useProjectTour } from './ProjectManagementTourContext';
import { getSafeTarget } from './tourSteps';

export default function ProjectManagementTour() {
    const { isDarkMode } = useTheme();
    const {
        isOpen,
        currentTourDefinition,
        currentStep,
        handleStepChange,
        closeTour,
        completeActiveTour,
    } = useProjectTour();

    // Map tour steps to Ant Design Tour format with safe target resolvers
    const antdSteps = useMemo(() => {
        if (!currentTourDefinition?.steps) return [];
        return currentTourDefinition.steps.map((s, idx) => ({
            title: (
                <div className="flex items-center gap-2.5 pb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg text-xs font-black bg-[#03488a]/10 dark:bg-[#00aaf1]/25 text-[#03488a] dark:text-[#00aaf1] shrink-0 shadow-2xs">
                        {idx + 1}
                    </span>
                    <span className="font-extrabold text-base sm:text-[16px] tracking-tight text-slate-900 dark:text-slate-50 leading-snug">
                        {s.title}
                    </span>
                </div>
            ),
            description: (
                <div className="mt-1.5 text-sm sm:text-[14px] leading-relaxed text-slate-700 dark:text-slate-200 font-normal">
                    <p className="m-0">{s.description}</p>
                    {s.bulletPoints && (
                        <ul className="mt-2 space-y-1 list-disc list-inside text-xs sm:text-[13px] text-slate-600 dark:text-slate-300">
                            {s.bulletPoints.map((bp, i) => (
                                <li key={i}>{bp}</li>
                            ))}
                        </ul>
                    )}
                </div>
            ),
            target: getSafeTarget(s.selector, s.fallbackSelector),
            placement: s.placement || 'bottom',
            mask: true,
            arrow: true,
        }));
    }, [currentTourDefinition]);

    // Ant Design theme tokens tailored to DymaxTech light/dark aesthetics
    const tourTheme = useMemo(() => ({
        token: {
            colorPrimary: isDarkMode ? '#00AAF1' : '#03488A',
            borderRadius: 14,
            fontSize: 14,
            fontSizeHeading: 16,
            lineHeight: 1.6,
        },
        components: {
            Tour: {
                colorBgElevated: isDarkMode ? '#1e293b' : '#ffffff',
                colorText: isDarkMode ? '#f8fafc' : '#1e293b',
                colorTextDescription: isDarkMode ? '#94a3b8' : '#475569',
                colorWhite: isDarkMode ? '#0f172a' : '#ffffff',
                zIndexPopupBase: 1300,
            },
        },
    }), [isDarkMode]);

    if (!isOpen || antdSteps.length === 0) return null;

    return (
        <ConfigProvider theme={tourTheme}>
            <style>{`
                .ant-tour {
                    z-index: 1300 !important;
                }
                .ant-tour-inner {
                    max-width: 440px !important;
                    padding: 16px 20px !important;
                    box-shadow: 0 20px 35px -5px rgba(0, 0, 0, 0.25), 0 10px 15px -5px rgba(0, 0, 0, 0.1) !important;
                    border-radius: 16px !important;
                }
                .ant-tour-title {
                    font-size: 16px !important;
                    line-height: 1.4 !important;
                }
                .ant-tour-description {
                    font-size: 14px !important;
                    line-height: 1.65 !important;
                }
                .ant-tour-footer {
                    margin-top: 14px !important;
                }
                .ant-tour-footer .ant-btn {
                    border-radius: 8px !important;
                    font-weight: 600 !important;
                    font-size: 13px !important;
                    height: 32px !important;
                    padding: 4px 14px !important;
                }
            `}</style>
            <Tour
                open={isOpen}
                onClose={closeTour}
                current={currentStep}
                onChange={handleStepChange}
                onFinish={completeActiveTour}
                steps={antdSteps}
                indicatorsRender={(current, total) => (
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                        {current + 1} of {total}
                    </span>
                )}
            />
        </ConfigProvider>
    );
}
